import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { apiV2 } from "api/firebase";
import calculatePriceWithFees, {
  CalculatePriceWithFeesArgs,
} from "tools/order/calculatePriceWithFees";
import { toCents } from "tools/product/formats/formatCents";

export type QtyOverrideMap = Map<string, number | null>;

type State = {
  qtyOverrideMap: QtyOverrideMap;
  priceWithFeesMap: Map<string, number>;
  priceOverrideMap: Map<string, number | null>;
};

type UpdateQtyArgs = {
  configId?: string;
  value: number | null;
  showId: string;
  shouldSetLocally?: boolean;
};

type UpdateCpoConfigQty = {
  quantity: number | null;
  configId: string;
};

type UpdateCpoConfigsQty = {
  showId: string;
  values: UpdateCpoConfigQty[];
};

export type UpdateCpoConfigPriceWithFeesBody = {
  priceUplift: number;
  id: string;
};
type UpdatePriceArgs = UpdateQtyArgs &
  Omit<CalculatePriceWithFeesArgs, "showProductPrice"> & {
    shouldSetLocally?: boolean;
    customConfigs?: UpdateCpoConfigPriceWithFeesBody[];
  };

type Action = {
  updateQty: (args: UpdateQtyArgs) => Promise<void>;
  updateCpoConfigsQty: (args: UpdateCpoConfigsQty) => void;
  updatePrice: (args: UpdatePriceArgs) => Promise<void>;
  clearPriceWithFees: (showId: string) => void;
  getOverriddenPrice: (
    showId: string,
    configId: string
  ) => number | undefined | null;
};

export const getKeyShowProducts = (showId: string, configId: string) =>
  `${showId}-${configId}`;

export const useShowProducts = create(
  devtools<State & Action>(
    (set, get) => ({
      qtyOverrideMap: new Map(),
      priceWithFeesMap: new Map(),
      priceOverrideMap: new Map(),

      clearPriceWithFees: (showId) => {
        const showKeyPrefix = `${showId}-`;

        set((state) => {
          const priceWithFeesCopy = new Map(state.priceWithFeesMap.entries());

          state.priceWithFeesMap.forEach((_, key) => {
            if (key.startsWith(showKeyPrefix)) {
              priceWithFeesCopy.delete(key);
            }
          });

          if (priceWithFeesCopy.size === state.priceWithFeesMap.size) {
            return state;
          }

          return { priceWithFeesMap: priceWithFeesCopy };
        });
      },

      getOverriddenPrice: (showId: string, configId: string) => {
        return get().priceOverrideMap.get(getKeyShowProducts(showId, configId));
      },

      updateQty: async ({
        value,
        showId,
        configId = "",
        shouldSetLocally = false,
      }) => {
        if (!shouldSetLocally) {
          if (value === null) {
            await apiV2.customProductOption.override.removeQuantity({
              showId,
              cpoConfigId: configId,
            });
          } else {
            await apiV2.customProductOption.override.setQuantity({
              showId,
              cpoConfigId: configId,
              quantity: value,
            });
          }
        }

        set((state) => {
          const qtyCopy = new Map(state.qtyOverrideMap.entries());
          qtyCopy.set(getKeyShowProducts(showId, configId), value);
          return {
            qtyOverrideMap: qtyCopy,
          };
        });
      },

      updateCpoConfigsQty: ({ values, showId }) => {
        const qtyCopy = new Map(get().qtyOverrideMap);
        values.forEach(({ quantity, configId }) => {
          const key = getKeyShowProducts(showId, configId);
          qtyCopy.set(key, quantity);
        });

        set(() => ({ qtyOverrideMap: qtyCopy }));
      },

      updatePrice: async ({
        showId,
        configId = "",
        value,
        productPrice,
        showDeliveryStart,
        showSupplierFee,
        organiserFee,
        surcharges,
        shouldSetLocally,
        customConfigs,
      }) => {
        if (!shouldSetLocally) {
          if (typeof value === "number") {
            await apiV2.customProductOption.override.setPrice({
              cpoConfigId: configId,
              showId,
              amount: toCents(value),
            });
          } else {
            await apiV2.customProductOption.override.removePrice({
              showId,
              cpoConfigId: configId,
            });
          }
        }

        const priceWithFees = calculatePriceWithFees({
          productPrice,
          showProductPrice: value,
          organiserFee,
          showSupplierFee,
          showDeliveryStart,
          surcharges,
        });
        const currentState = get();
        const priceWithFeesCopy = new Map(
          currentState.priceWithFeesMap.entries()
        );
        const priceOverridesCopy = new Map(
          currentState.priceOverrideMap.entries()
        );

        const key = getKeyShowProducts(showId, configId);
        priceWithFeesCopy.set(key, priceWithFees);
        priceOverridesCopy.set(key, value);

        if (customConfigs?.length) {
          customConfigs.forEach((customConfig) => {
            const configKey = getKeyShowProducts(showId, customConfig.id);
            const configPriceUplift = customConfig.priceUplift ?? 0;
            const configPrice = (productPrice ?? 0) + configPriceUplift;
            const configShowPrice =
              typeof value === "number" ? value + configPriceUplift : null;

            const configPriceWithFees = calculatePriceWithFees({
              productPrice: configPrice,
              ...(configShowPrice !== null && {
                showProductPrice: configShowPrice,
              }),
              organiserFee,
              surcharges,
              showSupplierFee,
              showDeliveryStart,
            });

            priceWithFeesCopy.set(configKey, configPriceWithFees);
          });
        }

        set(() => ({
          priceOverrideMap: priceOverridesCopy,
          priceWithFeesMap: priceWithFeesCopy,
        }));
      },
    }),
    { name: "show-products" }
  )
);
